import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatarId?: string;
  coverPhotoId?: string;
  isSiteAdmin: boolean;
  profileVisibility?: string;
}

export interface Team {
  id: string;
  name: string;
  slug: string;
  logoId?: string;
  role: 'admin' | 'manager' | 'member';
}

interface AuthState {
  user: User | null;
  teams: Team[];
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => void;
  refreshAuth: () => Promise<boolean>;
  fetchUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  setTeams: (teams: Team[]) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      teams: [],
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        const res = await api.post<{
          token: string;
          refreshToken: string;
          user: User;
        }>('/api/auth/login', { email, password }, false);

        if (res.success && res.data) {
          localStorage.setItem('token', res.data.token);
          set({
            user: res.data.user,
            token: res.data.token,
            refreshToken: res.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });

          // Fetch user's teams
          await get().fetchUser();
          return true;
        }

        set({ error: res.error || 'Login failed', isLoading: false });
        return false;
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });

        const res = await api.post<{
          token: string;
          refreshToken: string;
          user: User;
        }>('/api/auth/register', data, false);

        if (res.success && res.data) {
          localStorage.setItem('token', res.data.token);
          set({
            user: res.data.user,
            token: res.data.token,
            refreshToken: res.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        }

        set({ error: res.error || 'Registration failed', isLoading: false });
        return false;
      },

      logout: () => {
        const { refreshToken } = get();
        if (refreshToken) {
          api.post('/api/auth/logout', { refreshToken });
        }

        localStorage.removeItem('token');
        set({
          user: null,
          teams: [],
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
      },

      refreshAuth: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;

        const res = await api.post<{
          token: string;
          refreshToken: string;
        }>('/api/auth/refresh', { refreshToken }, false);

        if (res.success && res.data) {
          localStorage.setItem('token', res.data.token);
          set({
            token: res.data.token,
            refreshToken: res.data.refreshToken,
          });
          return true;
        }

        // Refresh failed, logout
        get().logout();
        return false;
      },

      fetchUser: async () => {
        const res = await api.get<{
          user: User;
          teams: Team[];
        }>('/api/auth/me');

        if (res.success && res.data) {
          set({
            user: res.data.user,
            teams: res.data.teams,
          });
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user: User) => set({ user }),

      setTeams: (teams: Team[]) => set({ teams }),
    }),
    {
      name: 'driveitnq-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
